/**
 * Lazy Google Fonts: one css2 `<link>` per family, injected the first time a
 * screen needs the face and never again.
 *
 * `index.html` loads the app's own three faces up front; this is for the
 * catalogue faces a community design may name, of which a page usually needs
 * one or two. The stylesheet only declares `@font-face` rules — the browser
 * downloads a font file when text is actually set in it — so injecting a link
 * for a face that ends up off screen costs one small CSS request.
 */

const CSS2_ENDPOINT = 'https://fonts.googleapis.com/css2';

/** Families already asked for, so a second call is a no-op. */
const requested = new Set<string>();

/**
 * The css2 URL for one family at the given weights:
 * `?family=Playfair+Display:wght@400;700&display=swap`.
 *
 * The name is percent-encoded and its spaces written as `+`, the form Google
 * documents. The `:`, `@` and `;` of the axis spec stay literal, which the API
 * requires. Weights are whole numbers 1..1000, deduplicated and ascending —
 * the API answers 400 to an unsorted list.
 */
export function googleFontsHref(family: string, weights: readonly number[]): string {
  const name = encodeURIComponent(family.trim()).replace(/%20/g, '+');
  const axis = [...new Set(weights)]
    .filter((weight) => Number.isInteger(weight) && weight >= 1 && weight <= 1000)
    .sort((a, b) => a - b);
  const spec = axis.length > 0 ? `${name}:wght@${axis.join(';')}` : name;
  return `${CSS2_ENDPOINT}?family=${spec}&display=swap`;
}

/**
 * Makes sure the page has a stylesheet for `family`. Safe to call on every
 * render path and from many components at once: the first call per family
 * adds the link, and every later one returns at once.
 */
export function loadGoogleFont(family: string, weights: readonly number[]): void {
  const key = family.trim();
  if (key.length === 0 || requested.has(key) || typeof document === 'undefined') return;
  requested.add(key);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = googleFontsHref(key, weights);
  link.dataset.fontFamily = key;
  document.head.appendChild(link);
}
